import { Component, inject, input, linkedSignal, signal } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../../../../../core/utils";
import { Constants } from "../../../../../../../constants/constants";
import { PageMode } from "../../../../../../../constants/enums";
import { CardEntryInfoType } from "../../../../../../../models";
import { DataentryApiService, LanguageService } from "../../../../../../../services";
import { EditDataEntryModalComponent } from "../../../../../..";
import { haveAikpiItemsChanged } from "../aikpi-items-diff/aikpi-items-diff";
import { AikpiItemsViewComponent } from "../aikpi-items-diff/aikpi-items-view";
import { AikpiPeriodToggleComponent } from "../aikpi-items-diff/aikpi-period-toggle";
import type {
  BoundedNoFormulaMetricType,
  MetricDiffRecord,
  NoFormulaMetricType,
  UnboundedNoFormulaMetricType,
} from "../types";

@Component({
  selector: "adaa-no-formula-view",
  styleUrl: "../styles.scss",
  imports: [TranslatePipe, AikpiItemsViewComponent, AikpiPeriodToggleComponent],
  template: `
    <section class="row d-flex mt-2">
      <div
        class="fw-bold px-3 py-2 data-entry-col"
        [class.col-5]="!isNewEntries()"
        [class.col-4]="isNewEntries()"
      ></div>

      @if (!isForecastedValues() && !isNewEntries()) {
        <div class="col-1 fw-bold px-3 py-2 data-entry-col"></div>
      }
      <div
        class="col-3 fw-bold px-3 py-2 data-entry-col text-primary"
        [class.col-4]="isNewEntries()"
        [class.col-7]="isForecastedValues()"
      >
        {{ (isForecastedValues() ? "forecasted_values.title_plural" : "data_entry.actual") | translate }}
      </div>
      @if (!isForecastedValues()) {
        <div class="col-3 fw-bold px-3 py-2 data-entry-col text-primary" [class.col-4]="isNewEntries()">
          {{ "data_entry.target" | translate }}
        </div>
      }
    </section>

    <section class="row d-flex">
      <div
        class="fw-bold px-3 py-2 data-entry-col"
        [class.col-5]="!isNewEntries()"
        [class.col-4]="isNewEntries()"
      ></div>
      @if (!isForecastedValues() && !isNewEntries()) {
        <div class="col-1 fw-bold px-3 py-2 data-entry-col"></div>
      }
      <div class="col-3 fw-bold" [class.col-4]="isNewEntries()" [class.col-7]="isForecastedValues()">
        <div class="row d-flex text-primary">
          <div class="col-6 px-3 py-2 data-entry-col">
            {{ "communication.difference.new_value" | translate }}
          </div>
          <div class="col-6 px-3 py-2 data-entry-col">
            {{ "communication.difference.old_value" | translate }}
          </div>
        </div>
      </div>

      @if (!isForecastedValues()) {
        <div
          class="col-3"
          [class.px-3]="!isBounded()"
          [class.py-2]="!isBounded()"
          [class.data-entry-col]="!isBounded()"
          [class.col-4]="isNewEntries()"
        >
          @if (isBounded()) {
            <div class="row d-flex">
              <div class="col-6 fw-bold px-3 py-2 data-entry-col text-primary">{{ "kpi.lower_limit" | translate }}</div>
              <div class="col-6 fw-bold px-3 py-2 data-entry-col text-primary">{{ "kpi.upper_limit" | translate }}</div>
            </div>
          }
        </div>
      }
    </section>

    @for (data of processedData(); track data.new.id) {
      <div class="period-group" [class.striped]="$even">
        <section class="row d-flex">
          <div class="fw-bold px-3 py-2 data-entry-col" [class.col-5]="!isNewEntries()" [class.col-4]="isNewEntries()">
            @if (hasItems(data)) {
              <adaa-aikpi-period-toggle
                [label]="getPeriodLabel(data.new)"
                [expanded]="expandedPeriods().has($index)"
                (toggled)="togglePeriod($index)"
              />
            } @else {
              {{ getPeriodLabel(data.new) }}
            }
          </div>

          @if (!isForecastedValues() && !isNewEntries()) {
            <div
              class="col-1 fw-bold px-3 py-0 data-entry-col d-flex justify-content-center align-content-center align-items-center"
            >
              <i class="adaa-icon-view1 fs-3 cursor-pointer" role="button" (click)="viewDifferences(data.new)"></i>
            </div>
          }

          <div class="col-3" [class.col-7]="isForecastedValues()" [class.col-4]="isNewEntries()">
            <div class="row d-flex">
              <div
                class="col-6 px-3 py-2 data-entry-col"
                dir="ltr"
                [class.has-diff]="showOnlyNew() || data.new.wfProcessCtlId || data.hasDiff"
              >
                @if (showNAFlag(+$index, "new")) {
                  {{ "N/A" }}
                } @else {
                  {{ data.new.actual }} &nbsp;
                }
              </div>

              <div
                class="col-6 px-3 py-2 data-entry-col"
                dir="ltr"
                [class.has-diff]="data.old?.wfProcessCtlId || data.hasDiff"
              >
                @if (showNAFlag(+$index, "old")) {
                  {{ "N/A" }}
                } @else {
                  {{ data.old?.actual }} &nbsp;
                }
              </div>
            </div>
          </div>

          @if (!isForecastedValues()) {
            <div
              class="col-3"
              [class.px-3]="!isBounded()"
              [class.py-2]="!isBounded()"
              [class.data-entry-col]="!isBounded()"
              [class.col-4]="isNewEntries()"
            >
              @if (isBounded()) {
                <div class="row d-flex">
                  <div class="col-6 px-3 py-2 data-entry-col">{{ getBoundedValue("lower", data.new) }} &nbsp;</div>
                  <div class="col-6 px-3 py-2 data-entry-col">{{ getBoundedValue("high", data.new) }} &nbsp;</div>
                </div>
              } @else {
                {{ getUnboundedValue(data.new) }} &nbsp;
              }
            </div>
          }
        </section>

        @if (hasItems(data) && expandedPeriods().has($index)) {
          <section class="row d-flex">
            <div class="col-12 px-3 py-2 period-items">
              <div class="fw-bold text-primary mb-2">
                {{ "data_entry.period" | translate }}: {{ getPeriodLabel(data.new) }}
              </div>
              <adaa-aikpi-items-view [newItems]="data.newItems" [oldItems]="data.oldItems" />
            </div>
          </section>
        }
      </div>
    }
  `,
})
export class NoFormulaViewComponent {
  private _translateService = inject(TranslateService);
  private _languageService = inject(LanguageService);
  private _modalService = inject(NgbModal);
  private _dataentryApiService = inject(DataentryApiService);

  isBounded = input<boolean>();
  showOnlyNew = input<boolean>();
  baselineYear = input<number>();
  isBaselineKpi = input<boolean>();
  newMetrics = input<NoFormulaMetricType[]>([]);
  oldMetrics = input<NoFormulaMetricType[]>([]);
  isForecastedValues = input<boolean>(false);
  isNewEntries = input<boolean>(false);
  isItemsEntry = input<boolean>(false);
  isAuditTrail = input<boolean>(false);

  processedData = signal<MetricDiffRecord[]>([]);
  expandedPeriods = linkedSignal<ReadonlySet<number>>(
    () => new Set(this.processedData().flatMap((data, index) => (data.hasDiff ? [index] : [])))
  );

  public ngOnInit() {
    this._processData();
  }

  public getUnboundedValue(metric: NoFormulaMetricType) {
    return this._getBaselineValue(metric.year, (metric as UnboundedNoFormulaMetricType).target);
  }

  public getBoundedValue(type: "lower" | "high", metric: NoFormulaMetricType) {
    let target: number;
    switch (type) {
      case "lower": {
        target = (metric as BoundedNoFormulaMetricType).lowerLimit;
        break;
      }

      case "high": {
        target = (metric as BoundedNoFormulaMetricType).highLimit;
        break;
      }
    }

    return this._getBaselineValue(metric.year, target);
  }

  public showNAFlag(metricIndex: number, version: "old" | "new") {
    const metrics = version === "old" ? this.oldMetrics() : this.newMetrics();
    if (metrics == null) return false;

    const metric = metrics.find((_: NoFormulaMetricType, index: number) => metricIndex == index);
    if (!metric) return false;

    return metric.ignored;
  }

  public getPeriodLabel(metric: NoFormulaMetricType) {
    if (metric.frequency === Constants.FREQUENCY.MONTHLY) {
      return `${AdaaHelper.getMonthName(metric.month, AdaaHelper.getCurrentLang(), false)} ${metric.year}`;
    }

    if (metric.frequency === Constants.FREQUENCY.QUARTERLY) {
      return `${metric.year} ${this._translateService.instant(`data_entry.q${metric.quarter}`)}`;
    }

    if (metric.frequency === Constants.FREQUENCY.SEMIANNUAL) {
      return `${this._translateService.instant(`targets.periods.semestral.${metric.semester}`)} ${metric.year}`;
    }

    return metric.year;
  }

  private _getBaselineValue(year: number, value: number) {
    if (year === this.baselineYear() && this.isBaselineKpi()) {
      return this._translateService.instant("kpi.baselineYear");
    }
    return value;
  }

  public viewDifferences(metric: NoFormulaMetricType) {
    this._dataentryApiService.getAnalysisDifferencesInfo(metric.itemId, metric.periodId, metric.itemType).subscribe({
      next: (response) => this._openEditDataEntryModal(this.getPeriodLabel(metric), response.responseData),
    });
  }

  private _openEditDataEntryModal(periodLabel: string | number | undefined, dataEntry: CardEntryInfoType): void {
    const modal = this._modalService.open(EditDataEntryModalComponent, {
      centered: true,
      size: "lg",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = `${this._translateService.instant("data_entry.period")}: ${periodLabel}`;
    modal.componentInstance.dataEntry = dataEntry;
    modal.componentInstance.pageMode = PageMode.view;
    modal.componentInstance.showSubmit = false;
  }

  public hasItems(data: MetricDiffRecord) {
    if (!this.isItemsEntry()) return false;

    return data.new.actual != null || data.old?.actual != null;
  }

  public togglePeriod(index: number) {
    this.expandedPeriods.update((expanded) => {
      const next = new Set(expanded);
      if (next.has(index)) {
        next.delete(index);
        return next;
      }

      next.add(index);
      return next;
    });
  }

  private _getNewItems(newItem: NoFormulaMetricType, oldItem: NoFormulaMetricType | undefined) {
    if (this.isAuditTrail() || newItem.wfProcessCtlId) return newItem.operationStrategicTasks ?? [];

    return oldItem?.operationStrategicTasks ?? [];
  }

  private _processData() {
    const result = [];

    const newData = this.newMetrics() ?? [];
    const oldData = this.oldMetrics() ?? [];

    for (const newItem of newData) {
      const oldItem = oldData.find((data) => data.id === newItem.id);
      const newItems = this._getNewItems(newItem, oldItem);
      const oldItems = oldItem?.operationStrategicTasks ?? [];

      result.push({
        new: { ...newItem },
        ...(oldItem && { old: { ...oldItem } }),
        hasDiff: this._hasDiff(newItem, oldItem) || haveAikpiItemsChanged(newItems, oldItems),
        newItems,
        oldItems,
      });
    }

    this.processedData.set(result);
  }

  private _hasDiff(newItem: NoFormulaMetricType, oldItem: NoFormulaMetricType | undefined) {
    if (!newItem?.actual && !oldItem?.actual) return false;
    if (!oldItem) return true;

    return newItem.actual !== oldItem.actual;
  }
}
